import { useState, useEffect } from 'react'
import { useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom'
import Sidebar from '../components/dashboard/Sidebar.jsx'
import Header from '../components/dashboard/Header.jsx'
import DashboardHome from '../components/dashboard/DashboardHome.jsx'
import SettingsHome from '../components/settings/SettingsHome.jsx'
import BusinessProfileHome from '../components/settings/BusinessProfileHome.jsx'
import WhatsAppCreditsPage from '../components/settings/WhatsAppCreditsPage.jsx'
import BackupInformationPage from '../components/settings/BackupInformationPage.jsx'
import DeleteKhataPage from '../components/settings/DeleteKhataPage.jsx'
import PremiumPlansPage from '../components/settings/PremiumPlansPage.jsx'
import LogoutPage from '../components/logout/LogoutPage.jsx'
import PartiesHome from '../components/parties/PartiesHome.jsx'
import CashbookHome from '../components/cashbook/CashbookHome.jsx'
import ReportsHome from '../components/reports/ReportsHome.jsx'
import RemindersHome from '../components/reminders/RemindersHome.jsx'
import SharedKhataHome from '../components/shared_khata/SharedKhataHome.jsx'
import ItemsHome from '../components/items/ItemsHome.jsx'
import BillsHome from '../components/bills/BillsHome.jsx'
import HelpSupportHome from '../components/help/HelpSupportHome.jsx'
import { getBusinessProfile } from '../services/dashboardApi'
import { User, Settings, LogOut, ChevronRight, HelpCircle } from 'lucide-react'
import { useLang } from '../core/lang'
import LangKeys from '../core/locales/keys'


function Dashboard() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useLang()

  const [activeTab, setActiveTab] = useState('Dashboard')
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [menuOpen, setMenuOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(false)

  const [premiumLevel, setPremiumLevel] = useState(() => parseInt(localStorage.getItem("moxpal_premium_level") || "0"))
  const [isPremium, setIsPremium] = useState(() => localStorage.getItem("moxpal_is_premium") === "true")
  const [premiumValidTill, setPremiumValidTill] = useState(null)
  const [premiumDaysLeft, setPremiumDaysLeft] = useState(null)

  // Mapping of URL paths to Tab names
  const pathToTab = {
    '/dashboard': 'Dashboard',
    '/dashboard/parties': 'Parties',
    '/dashboard/cashbook': 'Cashbook',
    '/dashboard/bills': 'Bills',
    '/dashboard/items': 'Items',
    '/dashboard/reports': 'Reports',
    '/dashboard/reminders': 'Reminders',
    '/dashboard/help-support': 'Help & Support',
    '/dashboard/settings': 'Settings',
    '/dashboard/business-profile': 'Business Profile',
    '/dashboard/whatsapp-credits': 'WhatsApp Credits',
    '/dashboard/backup': 'Backup Information',
    '/dashboard/delete-khata': 'Delete Khata',
    '/dashboard/premium': 'Premium Plans',
    '/dashboard/logout': 'Logout'
  }

  // Mapping of Tab names to URL paths
  const tabToPath = {
    'Dashboard': '/dashboard',
    'Parties': '/dashboard/parties',
    'Cashbook': '/dashboard/cashbook',
    'Bills': '/dashboard/bills',
    'Items': '/dashboard/items',
    'Reports': '/dashboard/reports',
    'Reminders': '/dashboard/reminders',
    'Help & Support': '/dashboard/help-support',
    'Settings': '/dashboard/settings',
    'Business Profile': '/dashboard/business-profile',
    'WhatsApp Credits': '/dashboard/whatsapp-credits',
    'Backup Information': '/dashboard/backup',
    'Delete Khata': '/dashboard/delete-khata',
    'Premium Plans': '/dashboard/premium',
    'Logout': '/dashboard/logout'
  }

  // Sync state with URL on load and on location change
  useEffect(() => {
    const currentPath = location.pathname
    if (pathToTab[currentPath]) {
      setActiveTab(pathToTab[currentPath])
    }
  }, [location.pathname])

  // Function to handle tab changes and navigate
  const handleTabChange = (tab) => {
    if (tabToPath[tab]) {
      navigate(tabToPath[tab])
    }
    setActiveTab(tab)
  }
  const [showDropdown, setShowDropdown] = useState(false)
  const [profile, setProfile] = useState(null)

  const getInitial = () => {
    if (profile?.business_name) return profile.business_name.charAt(0).toUpperCase();
    return 'B';
  };

  useEffect(() => {
    fetchProfile()
    fetchPremiumStatus()
    const handleResize = () => {
      const mobile = window.innerWidth < 768
      setIsMobile(mobile)

      if (!mobile) {
        setMenuOpen(false)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const fetchProfile = async () => {
    try {
      const data = await getBusinessProfile()
      if (data?.success) {
        setProfile(data.data)
      }
    } catch (error) {
      console.error("Profile fetch error:", error)
    }
  }

  const calculateDaysLeft = (validTill) => {
    if (validTill) {
      try {
        const expiry = new Date(validTill.replace(/-/g, "/"));
        const now = new Date();
        const diffMs = expiry.getTime() - now.getTime();
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
        return diffDays > 0 ? diffDays : 0;
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  const fetchPremiumStatus = async () => {
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("login_token") || "";
      if (!token) return;
      const res = await fetch(`https://khata.moxpal.com/api/v2/index.php?path=whatsapp/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (json.success && json.data) {
        const d = json.data;
        const premiumLevelVal = parseInt(d.is_premium || 0);
        const serverIsPremium = premiumLevelVal > 0;
        const serverValidTill = d.premium_valid_till;

        setIsPremium(serverIsPremium);
        setPremiumLevel(premiumLevelVal);
        setPremiumValidTill(serverValidTill);

        localStorage.setItem("moxpal_premium_level", premiumLevelVal.toString());
        localStorage.setItem("moxpal_is_premium", serverIsPremium.toString());

        if (serverIsPremium) {
          let calculatedPlanId = 97;
          if (premiumLevelVal === 1) {
            calculatedPlanId = 96;
          } else if (premiumLevelVal === 2) {
            calculatedPlanId = 97;
          } else if (premiumLevelVal === 3) {
            calculatedPlanId = 98;
          }
          localStorage.setItem("premium_plan_id", calculatedPlanId.toString());
        }

        if (serverValidTill) {
          const days = calculateDaysLeft(serverValidTill);
          setPremiumDaysLeft(days);
        } else {
          setPremiumDaysLeft(null);
        }
      }
    } catch (e) {
      console.error("Premium check error:", e);
    }
  };




  return (
    <div style={styles.page}>
      <style>{`
        @keyframes popupGoldPulse {
          0% { transform: scale(1); box-shadow: 0 8px 20px rgba(245, 158, 11, 0.2); }
          50% { transform: scale(1.06); box-shadow: 0 12px 30px rgba(245, 158, 11, 0.4); }
          100% { transform: scale(1); box-shadow: 0 8px 20px rgba(245, 158, 11, 0.2); }
        }
      `}</style>
      {isMobile ? (
        <>
          {menuOpen && (
            <div style={styles.overlay} onClick={() => setMenuOpen(false)} />
          )}

          <Sidebar
            activeTab={activeTab}
            setActiveTab={(tab) => {
              handleTabChange(tab)
              setMenuOpen(false)
            }}
            isMobile={true}
            menuOpen={menuOpen}
            collapsed={collapsed}
            setCollapsed={setCollapsed}
            isPremium={isPremium}
            premiumLevel={premiumLevel}
            premiumValidTill={premiumValidTill}
            premiumDaysLeft={premiumDaysLeft}
          />
        </>
      ) : (
        <Sidebar
          activeTab={activeTab}
          setActiveTab={handleTabChange}
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          isPremium={isPremium}
          premiumLevel={premiumLevel}
          premiumValidTill={premiumValidTill}
          premiumDaysLeft={premiumDaysLeft}
        />
      )}

      <div
        className="main-content"
        style={{
          ...styles.main,
          marginLeft: isMobile ? '0' : (collapsed ? '80px' : '290px'),
          width: isMobile ? '100%' : (collapsed ? 'calc(100% - 80px)' : 'calc(100% - 290px)'),
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <Header
          title={activeTab}
          isMobile={isMobile}
          onMenuClick={() => setMenuOpen(true)}
          setActiveTab={handleTabChange}
          showDropdown={showDropdown}
          setShowDropdown={setShowDropdown}
          profile={profile}
        />

        <div className="print-content-wrap" style={styles.contentWrap}>
          <Routes>
            <Route path="/" element={<DashboardHome setActiveTab={handleTabChange} />} />
            <Route path="/parties" element={<PartiesHome setActiveTab={handleTabChange} />} />
            <Route path="/cashbook" element={<CashbookHome />} />
            <Route path="/bills" element={
               premiumLevel >= 2 ? (
                 <BillsHome setActiveTab={handleTabChange} profile={profile} />
               ) : (
                 <div style={{ position: 'relative', width: '100%', minHeight: 'calc(100vh - 80px)', overflow: 'hidden' }}>
                   <div style={{ filter: 'blur(6px)', pointerEvents: 'none', userSelect: 'none' }}>
                     <BillsHome setActiveTab={handleTabChange} profile={profile} />
                   </div>
                   <PremiumLockView tabName="Bills" onUpgrade={() => handleTabChange('Premium Plans')} onGoBack={() => handleTabChange('Dashboard')} />
                 </div>
               )
             } />
            <Route path="/items" element={
               premiumLevel >= 2 ? (
                 <ItemsHome />
               ) : (
                 <div style={{ position: 'relative', width: '100%', minHeight: 'calc(100vh - 80px)', overflow: 'hidden' }}>
                   <div style={{ filter: 'blur(6px)', pointerEvents: 'none', userSelect: 'none' }}>
                     <ItemsHome />
                   </div>
                   <PremiumLockView tabName="Items" onUpgrade={() => handleTabChange('Premium Plans')} onGoBack={() => handleTabChange('Dashboard')} />
                 </div>
               )
             } />
            <Route path="/reports" element={<ReportsHome setActiveTab={handleTabChange} />} />
            <Route path="/reminders" element={<RemindersHome userProfile={profile} />} />
            <Route path="/help-support" element={<HelpSupportHome userProfile={profile} />} />
            <Route path="/settings" element={<SettingsHome setActiveTab={handleTabChange} />} />
            <Route path="/business-profile" element={<BusinessProfileHome />} />
            <Route path="/whatsapp-credits" element={<WhatsAppCreditsPage />} />
            <Route path="/backup" element={<BackupInformationPage />} />
            <Route path="/delete-khata" element={<DeleteKhataPage businessName={localStorage.getItem("business_name") || ""} />} />
            <Route path="/premium" element={<PremiumPlansPage onBack={() => handleTabChange('Settings')} onUpgradeSuccess={fetchPremiumStatus} />} />
            <Route path="/logout" element={<LogoutPage />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </div>
      </div>

      {/* PORTAL-LIKE DROPDOWN RENDERED AT ROOT */}
      {showDropdown && (
        <>
          <div
            style={{
              ...styles.backdrop,
              backgroundColor: isMobile ? 'rgba(15, 23, 42, 0.6)' : 'transparent'
            }}
            onClick={() => setShowDropdown(false)}
          />
          <div style={{
            ...styles.dropdown,
            ...(isMobile ? styles.mobileDropdown : {})
          }}>
            {/* MOBILE HANDLE */}
            {isMobile && <div style={styles.mobileHandle} />}

            {/* DROPDOWN HEADER */}
            <div style={styles.dropdownHeader}>
              <div style={styles.largeAvatarWrapper}>
                {profile?.logo_url ? (
                  <img
                    src={profile.logo_url}
                    alt="Business Logo"
                    style={styles.largeAvatar}
                  />
                ) : (
                  <div style={styles.largeInitialAvatar}>
                    {getInitial()}
                  </div>
                )}
              </div>
              <div style={styles.headerInfo}>
                <div style={styles.headerName}>
                  {profile?.business_name || 'Moxpal Khata'}
                </div>
                <div style={styles.headerEmail}>
                  {profile?.owner_name || 'Business Owner'}
                </div>
              </div>
            </div>

            <div style={styles.divider} />

            {/* MENU ITEMS */}
            <div style={styles.menuList}>
              <button
                style={styles.menuItem}
                onClick={() => {
                  handleTabChange('Business Profile');
                  setShowDropdown(false);
                }}
              >
                <div style={styles.menuIconBox}>
                  <User size={18} />
                </div>
                <span style={styles.menuText}>{t(LangKeys.profile) || 'Edit Profile'}</span>
              </button>

              <button
                style={styles.menuItem}
                onClick={() => {
                  handleTabChange('Help & Support');
                  setShowDropdown(false);
                }}
              >
                <div style={styles.menuIconBox}>
                  <HelpCircle size={18} />
                </div>
                <span style={styles.menuText}>{t(LangKeys.help_support_title) || 'Help & Support'}</span>
              </button>

              <button
                style={styles.menuItem}
                onClick={() => {
                  handleTabChange('Settings');
                  setShowDropdown(false);
                }}
              >
                <div style={styles.menuIconBox}>
                  <Settings size={18} />
                </div>
                <span style={styles.menuText}>{t(LangKeys.settings) || 'Settings'}</span>
              </button>
            </div>

            <div style={styles.divider} />

            {/* LOGOUT */}
            <button
              style={{ ...styles.menuItem, color: '#EF4444' }}
              onClick={() => {
                handleTabChange('Logout');
                setShowDropdown(false);
              }}
            >
              <div style={{ ...styles.menuIconBox, color: '#EF4444', background: '#FEF2F2' }}>
                <LogOut size={18} />
              </div>
              <span style={{ ...styles.menuText, fontWeight: '600' }}>{t(LangKeys.logout) || 'Logout'}</span>
            </button>
          </div>
        </>
      )}
    </div>
  )
}

const styles = {
  page: {
    width: '100%',
    minHeight: '100vh',
    backgroundColor: '#F8FAFC',
    fontFamily: "'Outfit', sans-serif",
    position: 'relative',
  },

  main: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },

  contentWrap: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
  },

  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.35)',
    zIndex: 999,
  },

  // DROPDOWN STYLES COPIED HERE
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9998,
  },

  dropdown: {
    position: 'fixed', // Use fixed for better desktop reliability too
    top: '78px',
    right: '15px',
    width: '280px',
    backgroundColor: '#FFFFFF',
    borderRadius: '20px',
    border: '1px solid rgba(226, 232, 240, 0.8)',
    boxShadow: '0 20px 40px rgba(15, 23, 42, 0.12)',
    padding: '8px',
    zIndex: 9999,
    animation: 'dropdownFade 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'flex',
    flexDirection: 'column',
  },

  mobileDropdown: {
    bottom: '0',
    top: 'auto',
    left: '0',
    right: '0',
    width: '100%',
    maxWidth: 'none',
    borderRadius: '32px 32px 0 0',
    boxShadow: '0 -10px 50px rgba(0, 0, 0, 0.3)',
    padding: '12px 20px 40px 20px',
    animation: 'mobileDropdownFade 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
  },

  mobileHandle: {
    width: '40px',
    height: '4px',
    backgroundColor: '#E2E8F0',
    borderRadius: '2px',
    margin: '0 auto 12px auto',
  },

  dropdownHeader: {
    padding: '12px 12px 16px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },

  largeAvatarWrapper: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    overflow: 'hidden',
    border: '2px solid #F1F5F9',
  },

  largeAvatar: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },

  largeInitialAvatar: {
    width: '100%',
    height: '100%',
    background: 'linear-gradient(135deg, #6366F1, #4338CA)',
    color: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: '600',
  },

  headerInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },

  headerName: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#0F172A',
  },

  headerEmail: {
    fontSize: '12px',
    color: '#64748B',
    fontWeight: '500',
  },

  divider: {
    height: '1px',
    backgroundColor: '#F1F5F9',
    margin: '4px 0',
  },

  menuList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    margin: '4px 0',
  },

  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 12px',
    borderRadius: '12px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    transition: 'all 0.2s ease',
    textDecoration: 'none',
    color: '#475569',
  },

  menuText: {
    fontSize: '14px',
    fontWeight: '500',
    flex: 1,
  },

  menuIconBox: {
    width: '32px',
    height: '32px',
    borderRadius: '10px',
    backgroundColor: '#F8FAFC',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#64748B',
    transition: 'all 0.2s ease',
  },
}

const lockTranslations = {
  en: {
    title: "Premium Feature Locked",
    desc: "Unlock professional GST Billing, Invoices, and Stock Inventory Management.",
    benefit1: "🧾 Professional GST & Non-GST Billing",
    benefit2: "📦 Complete Stock Inventory & Low Stock Alerts",
    benefit3: "💻 Full Computer / Desktop Access",
    benefit4: "👥 Manage multiple devices and staffs",
    upgradeBtn: "Upgrade to Ads Free+ / Premium 🚀",
    backBtn: "Go to Dashboard"
  },
  hi: {
    title: "प्रीमियम फ़ीचर लॉक है",
    desc: "व्यावसायिक GST बिलिंग, इनवॉइस और स्टॉक इन्वेंटरी प्रबंधन को अनलॉक करें।",
    benefit1: "🧾 व्यावसायिक GST और गैर-GST बिलिंग",
    benefit2: "📦 संपूर्ण स्टॉक इन्वेंटरी और कम स्टॉक अलर्ट",
    benefit3: "💻 कंप्यूटर / डेस्कटॉप पर पूर्ण एक्सेस",
    benefit4: "👥 मल्टी-डिवाइस लॉगिन और स्टाफ अनुमतियां",
    upgradeBtn: "Ads Free+ / Premium में अपग्रेड करें 🚀",
    backBtn: "डैशबोर्ड पर जाएं"
  },
  en_in: { // Hinglish
    title: "Premium Feature Locked Hai",
    desc: "Professional GST Billing, Invoices, aur Stock Inventory Management ko unlock karein.",
    benefit1: "🧾 Professional GST & Non-GST Billing",
    benefit2: "📦 Complete Stock Inventory & Low Stock Alerts",
    benefit3: "💻 Computer / Desktop par Full Access",
    benefit4: "👥 Multi-device login aur staff permissions",
    upgradeBtn: "Upgrade to Ads Free+ / Premium 🚀",
    backBtn: "Dashboard par Jayein"
  },
  gu: {
    title: "પ્રથમ પ્લસ લોક છે",
    desc: "વ્યાવસાયિક GST બિલિંગ, ઇનવોઇસ અને સ્ટોક ઇન્વેન્ટરી મેનેજમેન્ટને અનલૉક કરો.",
    benefit1: "🧾 વ્યવસાયિક GST અને નોન-GST બિલિંગ",
    benefit2: "📦 સંપૂર્ણ સ્ટોક ઇન્વેન્ટરી અને ઓછો સ્ટોક ચેતવણીઓ",
    benefit3: "💻 કમ્પ્યુટર / ડેસ્કટોપ પર સંપૂર્ણ એક્સેસ",
    benefit4: "👥 મલ્ટી-ડિવાઇસ લોગિન અને સ્ટાફ પરવાનગીઓ",
    upgradeBtn: "Ads Free+ / Premium માં અપગ્રેડ કરો 🚀",
    backBtn: "ડેશબોર્ડ પર જાઓ"
  },
  mr: {
    title: "प्रीमियम वैशिष्ट्य लॉक आहे",
    desc: "व्यावसायिक GST बिलिंग, इनव्हॉइस आणि स्टॉक इन्व्हेंटरी व्यवस्थापन अनलॉक करा.",
    benefit1: "🧾 व्यावसायिक GST आणि बिगर-GST बिलिंग",
    benefit2: "📦 संपूर्ण स्टॉक इन्व्हेंटरी आणि कमी स्टॉक अलर्ट",
    benefit3: "💻 संगणक / डेस्कटॉपवर पूर्ण प्रवेश",
    benefit4: "👥 मल्टि-डिव्हाइस लॉगिन आणि स्टाफ परवानग्या",
    upgradeBtn: "Ads Free+ / Premium मध्ये अपग्रेड करा 🚀",
    backBtn: "डॅशबोर्डवर जा"
  },
  pa: {
    title: "ਪ੍ਰੀਮੀਅਮ ਫੀਚਰ ਲਾਕ ਹੈ",
    desc: "ਪੇਸ਼ੇਵਰ GST ਬਿਲਿੰਗ, ਇਨਵੌਇਸ ਅਤੇ ਸਟਾਕ ਇਨਵੈਂਟਰੀ ਪ੍ਰਬੰਧਨ ਨੂੰ ਅਨਲੌਕ ਕਰੋ.",
    benefit1: "🧾 ਪੇਸ਼ੇਵਰ GST ਅਤੇ ਗੈਰ-GST ਬਿਲਿੰਗ",
    benefit2: "📦 ਪੂਰੀ ਸਟਾਕ ਇਨਵੈਂਟਰੀ ਅਤੇ ਘੱਟ ਸਟਾਕ ਅਲਰਟ",
    benefit3: "💻 ਕੰਪਿਊਟਰ / ਡੈਸਕਟਾਪ 'ਤੇ ਪੂਰੀ ਪਹੁੰਚ",
    benefit4: "👥 ਮਲਟੀ-ਡਵਾਈਸ ਲੌਗਇਨ ਅਤੇ ਸਟਾਫ ਇਜਾਜ਼ਤਾਂ",
    upgradeBtn: "Ads Free+ / Premium ਵਿੱਚ ਅੱਪਗ੍ਰੇਡ ਕਰੋ 🚀",
    backBtn: "ਡੈਸ਼ਬੋਰਡ 'ਤੇ ਜਾਓ"
  },
  ta: {
    title: "பிரீமியம் அம்சம் பூட்டப்பட்டுள்ளது",
    desc: "தொழில்முறை GST பில்லிங், இன்வாய்ஸ்கள் மற்றும் பங்கு சரக்கு நிர்வாகத்தை அன்லாக் செய்யவும்.",
    benefit1: "🧾 தொழில்முறை GST & நான்-GST பில்லிங்",
    benefit2: "📦முழுமையான பங்கு சரக்கு & குறைந்த பங்கு எச்சரிக்கைகள்",
    benefit3: "💻 கணினி / டெஸ்க்டாப்பில் முழு அணுகல்",
    benefit4: "👥 பல சாதன உள்நுழைவு மற்றும் ஊழியர்கள் அனுமதிகள்",
    upgradeBtn: "Ads Free+ / Premium மேம்படுத்தவும் 🚀",
    backBtn: "டாஷ்போர்டிற்குச் செல்லவும்"
  },
  te: {
    title: "ప్రీమియం ఫీచర్ లాక్ చేయబడింది",
    desc: "ప్రొఫెషనల్ GST బిల్లింగ్, ఇన్‌వాయిస్‌లు మరియు స్టాక్ ఇన్వెంటరీ మేనేజ్‌మెంట్‌ను అన్‌లాక్ చేయండి.",
    benefit1: "🧾 ప్రొఫెషనల్ GST & నాన్-GST బిల్లింగ్",
    benefit2: "📦 ప్రొఫెషనల్ స్టాక్ ఇన్వెంటరీ & తక్కువ స్టాక్ హెచ్చరికలు",
    benefit3: "💻 కంప్యూటర్ / డెస్క్‌టాప్‌పై పూర్తి యాక్సెస్",
    benefit4: "👥 మల్టీ-డివైస్ లాగిన్ మరియు సిబ్బంది అనుమతులు",
    upgradeBtn: "Ads Free+ / Premium కు అప్‌గ్రేడ్ చేయండి 🚀",
    backBtn: "డాష్‌బోర్డ్‌కు వెళ్లండి"
  }
};

const lockStyles = {
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(15, 23, 42, 0.45)', // Premium dark glassmorphic overlay
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    boxSizing: 'border-box',
    padding: '20px',
    zIndex: 999,
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: '24px',
    border: '1px solid rgba(226, 232, 240, 0.8)',
    boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.25)', // Strong drop shadow for pop up modal
    padding: '40px',
    maxWidth: '520px',
    width: '100%',
    textAlign: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    animation: 'dropdownFade 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  crownContainer: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #FEF3C7, #FDE68A)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '16px',
    boxShadow: '0 8px 20px rgba(245, 158, 11, 0.2)',
    animation: 'popupGoldPulse 2.5s infinite ease-in-out',
  },
  crown: {
    fontSize: '40px',
  },
  lockBadge: {
    backgroundColor: '#FEE2E2',
    color: '#EF4444',
    fontSize: '12px',
    fontWeight: '700',
    padding: '4px 12px',
    borderRadius: '20px',
    marginBottom: '20px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  title: {
    fontSize: '22px',
    fontWeight: '800',
    color: '#0F172A',
    margin: '0 0 8px 0',
  },
  desc: {
    fontSize: '14px',
    color: '#64748B',
    lineHeight: '1.5',
    margin: '0 0 24px 0',
    fontWeight: '500',
  },
  benefitsList: {
    width: '100%',
    backgroundColor: '#F8FAFC',
    borderRadius: '16px',
    padding: '16px 20px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '28px',
    border: '1px solid #E2E8F0',
  },
  benefitRow: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '13.5px',
    color: '#334155',
    fontWeight: '600',
    textAlign: 'left',
  },
  btnRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '100%',
  },
  upgradeBtn: {
    width: '100%',
    padding: '14px',
    borderRadius: '12px',
    border: 'none',
    background: 'linear-gradient(135deg, #F59E0B, #D97706)',
    color: '#FFFFFF',
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(217, 119, 6, 0.3)',
    transition: 'all 0.2s ease',
    outline: 'none',
  },
  backBtn: {
    width: '100%',
    padding: '12px',
    borderRadius: '12px',
    border: '1px solid #E2E8F0',
    backgroundColor: '#FFFFFF',
    color: '#64748B',
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    outline: 'none',
  },
};

const PremiumMedalIcon = ({ size = 100 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 120 120"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      {/* Ultra Gold Gradient with High Specular Highlights */}
      <linearGradient id="premiumGold3D" x1="20" y1="20" x2="100" y2="100" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#FFF9C4" />
        <stop offset="15%" stopColor="#FDE047" />
        <stop offset="45%" stopColor="#EAB308" />
        <stop offset="70%" stopColor="#CA8A04" />
        <stop offset="90%" stopColor="#854D0E" />
        <stop offset="100%" stopColor="#451A03" />
      </linearGradient>

      {/* Reverse Gold Gradient for Beveling */}
      <linearGradient id="bevelGold" x1="100" y1="20" x2="20" y2="100" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#78350F" />
        <stop offset="30%" stopColor="#CA8A04" />
        <stop offset="70%" stopColor="#FEF08A" />
        <stop offset="100%" stopColor="#A16207" />
      </linearGradient>

      {/* Shackle Cylindrical Chrome Gold Gradient */}
      <linearGradient id="shackleGold" x1="30" y1="0" x2="90" y2="0" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#78350F" />
        <stop offset="20%" stopColor="#FBBF24" />
        <stop offset="40%" stopColor="#FEF9C3" />
        <stop offset="60%" stopColor="#F59E0B" />
        <stop offset="80%" stopColor="#B45309" />
        <stop offset="100%" stopColor="#451A03" />
      </linearGradient>

      {/* Rich Royal Navy/Midnight Inner Plate Gradient */}
      <linearGradient id="royalMidnight" x1="40" y1="45" x2="80" y2="95" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#1E1B4B" />
        <stop offset="50%" stopColor="#0B0F19" />
        <stop offset="100%" stopColor="#020617" />
      </linearGradient>

      {/* Crown Red Velvet Gradient */}
      <linearGradient id="royalVelvet" x1="60" y1="58" x2="60" y2="78" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#F43F5E" />
        <stop offset="60%" stopColor="#991B1B" />
        <stop offset="100%" stopColor="#4C0519" />
      </linearGradient>
    </defs>

    {/* Background Golden Sparkle Rings (Soft & Elegant) */}
    <circle cx="60" cy="65" r="48" stroke="url(#premiumGold3D)" strokeWidth="0.8" strokeDasharray="3 6" opacity="0.3" />
    <circle cx="60" cy="65" r="42" stroke="url(#bevelGold)" strokeWidth="1.2" opacity="0.2" />

    {/* Dynamic Light Sweep Trails (Visual Flair) */}
    <path d="M 22 58 C 26 28, 94 28, 98 58" stroke="url(#premiumGold3D)" strokeWidth="0.6" strokeDasharray="6 12" opacity="0.4" />
    <path d="M 16 68 C 26 102, 94 102, 104 68" stroke="url(#bevelGold)" strokeWidth="0.8" strokeDasharray="4 8" opacity="0.3" />

    {/* Glowing Shackle (Lock Loop) - Cylindrical Chrome Metal Effect */}
    <path
      d="M 42 50 L 42 32 C 42 20, 78 20, 78 32 L 78 50"
      stroke="url(#shackleGold)"
      strokeWidth="7"
      strokeLinecap="round"
    />
    {/* Inner highlight line to simulate high-shine cylindrical metal reflection */}
    <path
      d="M 42 50 L 42 32 C 42 20, 78 20, 78 32 L 78 50"
      stroke="#FFFDF0"
      strokeWidth="1.5"
      strokeLinecap="round"
      opacity="0.3"
    />

    {/* Main Outer Shield Padlock Body (3D Gold Shield) */}
    <path
      d="M 34 46 C 30 46, 26 48, 26 54 L 26 78 C 26 92, 36 102, 60 102 C 84 102, 94 92, 94 78 L 94 54 C 94 48, 90 46, 86 46 Z"
      fill="url(#premiumGold3D)"
    />

    {/* Outer Shield Inner Bevel Rim (Opposing Gold Gradient for Carved 3D Rim) */}
    <path
      d="M 36.5 49.5 C 33.5 49.5, 29.5 51.5, 29.5 56.5 L 29.5 77.5 C 29.5 89.5, 38.5 98.5, 60 98.5 C 81.5 98.5, 90.5 89.5, 90.5 77.5 L 90.5 56.5 C 90.5 51.5, 86.5 49.5, 83.5 49.5 Z"
      fill="url(#bevelGold)"
    />

    {/* Inner Midnight High-Contrast Plate */}
    <path
      d="M 39 52 C 37 52, 33 54, 33 58.5 L 33 76.5 C 33 87, 41 95, 60 95 C 79 95, 87 87, 87 76.5 L 87 58.5 C 87 54, 83 52, 81 52 Z"
      fill="url(#royalMidnight)"
    />

    {/* Velvet Red Cushion behind Crown */}
    <path
      d="M 45 74 C 44 65, 52 59, 60 59 C 68 59, 76 65, 75 74 Z"
      fill="url(#royalVelvet)"
      opacity="0.9"
    />

    {/* Incredibly Detailed Golden Royal Crown */}
    <g>
      {/* Base gold band of the crown */}
      <path
        d="M 44 74 C 44 74, 52 76.5, 60 76.5 C 68 76.5, 76 74, 76 74 L 75 70.5 C 75 70.5, 67 72.5, 60 72.5 C 53 72.5, 45 70.5, 45 70.5 Z"
        fill="url(#premiumGold3D)"
      />
      
      {/* Gems on the crown base band */}
      <circle cx="60" cy="74.5" r="1.3" fill="#EF4444" />
      <circle cx="53" cy="73.8" r="0.9" fill="#3B82F6" />
      <circle cx="67" cy="73.8" r="0.9" fill="#10B981" />
      <circle cx="49" cy="73.2" r="0.6" fill="#FFFFFF" />
      <circle cx="56" cy="74.2" r="0.6" fill="#FFFFFF" />
      <circle cx="64" cy="74.2" r="0.6" fill="#FFFFFF" />
      <circle cx="71" cy="73.2" r="0.6" fill="#FFFFFF" />

      {/* The 5 elegant gold peaks of the crown */}
      <path
        d="M 44 74 L 41 62 C 41 62, 45 64, 49 68 L 60 54 L 71 68 C 75 64, 79 62, 79 62 L 76 74 Q 60 77 44 74 Z"
        fill="url(#premiumGold3D)"
      />

      {/* Crown Peak Pearls / Diamonds */}
      <circle cx="60" cy="53" r="2.2" fill="url(#bevelGold)" />
      <circle cx="60" cy="53" r="1" fill="#FFFFFF" />
      
      {/* Center Peak Shining Diamond Starburst */}
      <path d="M 60 46 L 61.2 49.5 L 64.5 50.7 L 61.2 51.9 L 60 55.4 L 58.8 51.9 L 55.5 50.7 L 58.8 49.5 Z" fill="#FFFDF0" />

      <circle cx="41" cy="61" r="1.6" fill="#FFFFFF" />
      <circle cx="79" cy="61" r="1.6" fill="#FFFFFF" />
      <circle cx="49" cy="67" r="1.2" fill="#FFFFFF" />
      <circle cx="71" cy="67" r="1.2" fill="#FFFFFF" />
    </g>

    {/* Glowing Luxury Keyhole */}
    <g>
      {/* Subtle Gold aura around the keyhole */}
      <circle cx="60" cy="85.5" r="5" fill="url(#premiumGold3D)" opacity="0.3" />
      
      {/* Inner Keyhole structure */}
      <circle cx="60" cy="85" r="3.2" fill="url(#premiumGold3D)" />
      <path d="M 57.8 85 C 57.8 85, 56.5 91, 56.5 92.5 C 56.5 93, 57 93.5, 60 93.5 C 63 93.5, 63.5 93, 63.5 92.5 C 63.5 91, 62.2 85, 62.2 85 Z" fill="url(#premiumGold3D)" />
      
      {/* High-shine dot at center of keyhole */}
      <circle cx="60" cy="84.2" r="0.9" fill="#FFFDF0" />
    </g>

    {/* Hand-Crafted Glowing Diamond Sparkles Orbiting the Padlock */}
    {/* Sparkle 1: Top Left */}
    <path d="M 24 24 L 26.5 28.5 L 31 29.5 L 26.5 30.5 L 24 35 L 21.5 30.5 L 17 29.5 L 21.5 28.5 Z" fill="#FFF9C4" />
    
    {/* Sparkle 2: Top Right (Large and Brilliant) */}
    <path d="M 96 32 L 99 37 L 104 38 L 99 39 L 96 44 L 93 39 L 88 38 L 93 37 Z" fill="#FFFBEB" />
    
    {/* Sparkle 3: Bottom Left */}
    <path d="M 16 78 L 18 81 L 21 81.5 L 18 82 L 16 85 L 14 82 L 11 81.5 L 14 81 Z" fill="#FFF9C4" />
    
    {/* Sparkle 4: Bottom Right */}
    <path d="M 104 84 L 106 87 L 109 87.5 L 106 88 L 104 91 L 102 88 L 99 87.5 L 102 87 Z" fill="#FFF59D" />
    
    {/* Sparkle 5: Top Center (Perfect Accent) */}
    <path d="M 60 12 L 61.5 15 L 64 15.5 L 61.5 16 L 60 19 L 58.5 16 L 56 15.5 L 58.5 15 Z" fill="#FFFBEB" />
  </svg>
);

function PremiumLockView({ tabName, onUpgrade, onGoBack }) {
  const { lang } = useLang();
  const currentLang = lockTranslations[lang] ? lang : 'en';
  const tLock = (key) => lockTranslations[currentLang]?.[key] || lockTranslations.en[key];

  return (
    <div style={lockStyles.overlay}>
      <div style={lockStyles.card}>
        <div style={{
          marginBottom: '20px',
          animation: 'popupGoldPulse 2.5s infinite ease-in-out',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <PremiumMedalIcon size={85} />
        </div>

        <div style={lockStyles.lockBadge}>
          🔒 Locked
        </div>

        <h2 style={lockStyles.title}>{tLock('title')}</h2>
        <p style={lockStyles.desc}>{tLock('desc')}</p>

        <div style={lockStyles.benefitsList}>
          <div style={lockStyles.benefitRow}>
            <span>{tLock('benefit1')}</span>
          </div>
          <div style={lockStyles.benefitRow}>
            <span>{tLock('benefit2')}</span>
          </div>
          <div style={lockStyles.benefitRow}>
            <span>{tLock('benefit3')}</span>
          </div>
          <div style={lockStyles.benefitRow}>
            <span>{tLock('benefit4')}</span>
          </div>
        </div>

        <div style={lockStyles.btnRow}>
          <button style={lockStyles.upgradeBtn} onClick={onUpgrade}>
            {tLock('upgradeBtn')}
          </button>
          <button style={lockStyles.backBtn} onClick={onGoBack}>
            {tLock('backBtn')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Dashboard